import useApi from 'src/api/useApi';

// Strips releaseId from a queued request URL before replaying it. The Axios request interceptor appends
// the current releaseId to every outgoing request, so replaying a stored URL that already carries one
// would accumulate a second copy — and a failed replay persists that longer URL for the next cycle.
// Stripping here both prevents the growth and repairs URLs already corrupted in a user's IndexedDB.
const stripReleaseId = (url: string): string => {
    try {
        const urlObj = new URL(url);
        urlObj.searchParams.delete('releaseId');
        return urlObj.toString();
    } catch {
        return url;
    }
};

// Marks a request URL as a sync replay via a query param rather than a request header, since a custom
// header would need to be added to APIM's CORS allowed-headers policy (an infra change outside this
// app's deploy) to avoid failing preflight, whereas an extra query param needs no CORS allow-listing.
// Uses set() rather than append() so replaying an already-marked stored URL stays idempotent.
const markSyncReplay = (url: string): string => {
    try {
        const urlObj = new URL(url);
        urlObj.searchParams.set('syncReplay', 'true');
        return urlObj.toString();
    } catch {
        return url;
    }
};

const useSyncHelper = () => {
    const { postApiData } = useApi();

    const syncItems = async (getAllItems, deleteItem, requestKeys: string[], idKey: string) => {
        const items = await getAllItems();
        items.sort((a, b) => a.lastUpdated - b.lastUpdated);

        let i = 0;
        for (const item of items) {
            let allRequestsSuccessful = true;

            try {
                const matchingRequestKeys = requestKeys.filter((r) => item[r]);

                for (const requestKey of matchingRequestKeys) {
                    const request = item[requestKey];
                    if (request) {
                        const requests = Array.isArray(request) ? request : [request];
                        for (const req of requests) {
                            const response = await postApiData({
                                fullUrl: markSyncReplay(stripReleaseId(req.url)),
                                data: req.body,
                                skipRefreshCacheEndpoints: i < items.length - 1,
                                shouldReturnError: true,
                            });

                            if (!response || response instanceof Error) {
                                console.warn('Sync request failed, will retry next cycle.', item[idKey], response);
                                allRequestsSuccessful = false;
                                break;
                            }
                        }
                    }

                    if (!allRequestsSuccessful) {
                        break;
                    }
                }

                if (allRequestsSuccessful && matchingRequestKeys.length > 0) {
                    await deleteItem(item[idKey]);
                }
            } catch (error) {
                console.warn('Failed to sync item, will retry next cycle.', item[idKey], error);
            }
            i++;
        }
    };

    return {
        syncItems,
    };
};

export default useSyncHelper;
